<app-billytics-public-container-component>
  <!-- Hero Section -->
  <section class="pt-20" id="home">
    <div class="container mx-auto px-6 text-center">
      <h1 class="text-4xl font-bold mb-4">Keep your data alive. Turn static into strategic.</h1>
      <div class="flex justify-center">
        <p class="text-lg md:max-w-3xl mb-4">
          Connect your business systems. Apply rigorous analytical frameworks — from cost accounting
          to operational metrics. Get reports your leadership can rely on.
        </p>
      </div>
      <div class="flex flex-wrap gap-4 justify-center mt-4 mb-8">
        <button matButton="filled" (click)="openContactUs()">Request Custom Report</button>
        <button matButton="outlined" (click)="openAuth()" [matTooltip]="'Self-Serve Bookkeeping'">
          @if (firebaseUser$()) {
            <div class="flex items-center">
              <div class="pr-2">Open Dashboard</div>
              <div>
                @if (isLoading$()) {
                  <mat-progress-spinner class="small" mode="indeterminate"></mat-progress-spinner>
                }
              </div>
            </div>
          } @else {
            Start Free Trial
          }
        </button>
      </div>
    </div>
  </section>

  <!-- The Problem -->
  <section class="py-8">
    <div class="container mx-auto px-6">
      <h2 class="text-2xl font-semibold text-center mb-4">
        Your business deserves better than spreadsheets and generic dashboards
      </h2>
      <div class="mx-auto max-w-3xl">
        <p class="mb-6">
          Generic BI tools show you what happened. Spreadsheets break when your business changes.
          You need reports that:
        </p>
        <ul class="space-y-3 text-left mx-auto max-w-2xl">
          <li>• Apply rigorous frameworks — from cost accounting to operational analytics</li>
          <li>• Handle complex business structures (multi-entity, multiple cost centers)</li>
          <li>• Explain why numbers changed, not just show that they did</li>
          <li>• Connect operational metrics to financial outcomes</li>
          <!-- <li>• Can be defended to boards, auditors, and investors</li> -->
          <li>• Update automatically as your business scales and grows</li>
        </ul>
      </div>
    </div>
  </section>

  <!-- How It Works -->
  <section class="py-6 bg-surface-container-high">
    <div class="container mx-auto px-6">
      <h2 class="text-2xl font-semibold text-center mb-4">How It Works</h2>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
        <mat-card>
          <mat-card-content>
            <div class="space-y-3">
              <h3 class="text-lg font-semibold">1. Connect Your Data Sources</h3>
              <p class="">Pull data from your existing systems:</p>
              <ul class="space-y-1">
                <li>• ERP systems (SAP, NetSuite, Odoo)</li>
                <li>• Databases (Postgres, MySQL, SQL Server)</li>
                <li>• Business tools (Stripe, QuickBooks, Shopify)</li>
                <li>• Custom APIs and data warehouses</li>
              </ul>
              <p class="">We handle the extraction, normalization, and validation.</p>
            </div>
          </mat-card-content>
        </mat-card>
        <mat-card>
          <mat-card-content>
            <div class="space-y-3">
              <h3 class="text-lg font-semibold">2. Apply Rigorous Analytical Frameworks</h3>
              <p class="">
                We don't just visualize your data — we model it correctly using frameworks from cost
                accounting to operational analytics:
              </p>
              <ul class="space-y-1">
                <li>• Cost accounting (ABC, standard costing, variance analysis)</li>
                <li>• Operational metrics (efficiency, utilization, throughput)</li>
                <li>• Multi-entity consolidation and reconciliation</li>
                <li>• Custom business logic and allocation rules</li>
              </ul>
            </div>
          </mat-card-content>
        </mat-card>
        <mat-card>
          <mat-card-content>
            <div class="space-y-3">
              <h3 class="text-lg font-semibold">3. Get Reports You Can Rely On</h3>
              <p class="">Receive reports built for decision-making scrutiny:</p>
              <ul class="space-y-1">
                <li>• Custom P&L, operational dashboards, or integrated views</li>
                <li>• Drill-down capability to understand what drives the numbers</li>
                <li>• Root cause analysis for variances and anomalies</li>
                <li>• Export to Excel, PDF, or integrate via API</li>
              </ul>
            </div>
          </mat-card-content>
        </mat-card>
      </div>
    </div>
  </section>

  <!-- Use Cases -->
  <section class="py-6">
    <div class="container mx-auto px-6">
      <h2 class="text-2xl font-semibold text-center mb-4">Who This Is For</h2>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
        <mat-card>
          <mat-card-content>
            <div class="space-y-3">
              <h3 class="text-lg font-semibold">CFOs at Growing Companies</h3>
              <p class="mb-4">
                You've outgrown QuickBooks but SAP implementations take 18 months. We build the
                custom financial reports you need on your existing data — in weeks, not years.
              </p>
              <p class="font-semibold">Common requests:</p>
              <ul class="space-y-1">
                <li>• Multi-location P&L with proper cost allocation</li>
                <li>• Product-level profitability using ABC costing</li>
                <li>• Budget vs. actual with meaningful variance analysis</li>
              </ul>
            </div>
          </mat-card-content>
        </mat-card>
        <mat-card>
          <mat-card-content>
            <div class="space-y-3">
              <h3 class="text-lg font-semibold">Operations & Finance Teams</h3>
              <p class="mb-4">
                Connect operational metrics to financial outcomes. See how efficiency, utilization,
                and volume drive your P&L.
              </p>
              <p class="font-semibold">Use Cases:</p>
              <ul class="space-y-1">
                <li>• Cost per unit with operational drivers</li>
                <li>• Efficiency metrics tied to labour/overhead costs</li>
                <li>• Variance analysis by operational segment</li>
              </ul>
            </div>
          </mat-card-content>
        </mat-card>
        <mat-card>
          <mat-card-content>
            <div class="space-y-3">
              <h3 class="text-lg font-semibold">Companies Preparing for Audits or Fundraising</h3>
              <p class="mb-4">
                When your numbers will be scrutinized, you need reports built on defensible
                methodology — not dashboard screenshots.
              </p>
              <p class="font-semibold">We provide:</p>
              <ul class="space-y-1">
                <li>• Documentation of cost allocation methods</li>
                <li>• Audit trails for all calculations</li>
                <li>• Reconciliation to source systems</li>
                <li>• Methodology explanations for non-finance stakeholders</li>
              </ul>
            </div>
          </mat-card-content>
        </mat-card>
      </div>
    </div>
  </section>

  <!-- Two Ways to Start -->
  <section class="py-6 bg-surface-container-high" id="features">
    <div class="container mx-auto px-6">
      <h2 class="text-2xl font-semibold text-center mb-4">Two Ways to Start</h2>
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
        <!-- Path 1: Self-Serve Bookkeeping -->
        <mat-card class="flex flex-col">
          <mat-card-content class="flex-1">
            <div class="h-full flex flex-col">
              <div class="grow space-y-4">
                <h3 class="text-xl font-semibold">Path 1: Self-Serve Bookkeeping</h3>
                <p class="text-base font-bold">$10/month (2-month free trial)</p>

                <p class="font-semibold">Perfect for:</p>
                <ul class="space-y-1">
                  <li>• SMBs organizing financial data for the first time</li>
                  <li>• Companies building a data foundation before custom reporting</li>
                  <li>• Teams that need basic expense tracking and categorization</li>
                </ul>

                <p class="font-semibold mt-4">What you get:</p>
                <ul class="space-y-1">
                  <li>• Email-based receipt and bill capture</li>
                  <li>• Automatic categorization and vendor normalization</li>
                  <li>• Basic P&L, cash flow, and expense reports</li>
                  <li>• Export to Excel or share with your accountant</li>
                  <!-- <li>• Centralized dashboard for all financial documents</li> -->
                  <li>• Receipt scanning and upload</li>
                </ul>

                <div class="mt-6 bg-surface-container-low rounded">
                  <p class="font-semibold">The upgrade path:</p>
                  <p class="">
                    As your data grows and you need deeper analysis (ABC costing, multi-entity
                    consolidation, operational metrics), you can request custom reports built on the
                    same data foundation.
                  </p>
                </div>
              </div>

              <div class="text-center mt-6 w-full">
                <button
                  class="w-full"
                  matButton="filled"
                  (click)="openAuth()"
                  [matTooltip]="'Self-Serve Bookkeeping'">
                  @if (firebaseUser$()) {
                    <div class="flex items-center">
                      <div class="pr-2">Open Dashboard</div>
                      <div>
                        @if (isLoading$()) {
                          <mat-progress-spinner
                            class="small"
                            mode="indeterminate"></mat-progress-spinner>
                        }
                      </div>
                    </div>
                  } @else {
                    Start Free Trial
                  }
                </button>
              </div>
            </div>
          </mat-card-content>
        </mat-card>

        <!-- Path 2: Custom Enterprise Reports -->
        <mat-card class="flex flex-col border-2">
          <mat-card-content class="flex-1">
            <div class="space-y-4">
              <h3 class="text-xl font-semibold">Path 2: Custom Enterprise Reports</h3>
              <p class="text-base font-bold">Custom Pricing</p>

              <p class="font-semibold">Perfect for:</p>
              <ul class="space-y-1">
                <li>• Companies that need reports beyond what dashboards provide</li>
                <li>• CFOs preparing for audits, fundraising, or board meetings</li>
                <li>• Businesses with complex operations (multi-entity, multiple cost centers)</li>
                <li>• Teams that have outgrown QuickBooks but don't want SAP</li>
              </ul>

              <p class="font-semibold mt-4">What you get:</p>
              <ul class="space-y-1">
                <li>• Custom P&L built using the accounting method your business needs</li>
                <li>• ABC costing, standard costing, job costing, or custom allocation</li>
                <li>• Variance analysis with root cause breakdown</li>
                <li>• Multi-entity consolidation (if applicable)</li>
                <li>• Operational metrics tied to financial outcomes</li>
                <li>• Direct connections to your ERP, databases, or business tools</li>
              </ul>

              <div class="mt-6 bg-surface-container-low rounded">
                <p class="font-semibold">Delivery:</p>
                <p class="">
                  We scope your requirements, build the first report in 4-8 weeks, then maintain and
                  evolve it as your business changes.
                </p>
              </div>

              <div class="text-center mt-6 w-full">
                <button class="w-full" (click)="openContactUs()" matButton="filled">
                  Request Custom Report
                </button>
              </div>
            </div>
          </mat-card-content>
        </mat-card>
      </div>

      <!-- Why Start with Bookkeeping -->
      <!-- <div class="mt-12  mx-auto">
        <h3 class="text-xl font-semibold text-center mb-6">Why Start with Bookkeeping?</h3>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div class="p-4 bg-surface-container-low rounded">
            <p class="font-semibold mb-2">Clean data foundation</p>
            <p class="opacity-80">
              Your expenses, invoices, and transactions are already normalized and categorized
            </p>
          </div>
          <div class="p-4 bg-surface-container-low rounded">
            <p class="font-semibold mb-2">Faster custom report delivery</p>
            <p class="opacity-80">
              When you upgrade, we're not starting from scratch with messy data
            </p>
          </div>
          <div class="p-4 bg-surface-container-low rounded">
            <p class="font-semibold mb-2">Lower risk</p>
            <p class="opacity-80">
              Try the platform for $10/month before committing to custom work
            </p>
          </div>
          <div class="p-4 bg-surface-container-low rounded">
            <p class="font-semibold mb-2">Natural upgrade path</p>
            <p class="opacity-80">
              Many customers start tracking expenses, then realize they need ABC costing or
              multi-entity consolidation as they scale
            </p>
          </div>
        </div>
      </div> -->
    </div>
  </section>

  <!-- Pricing Plans -->
  <section class="py-6" id="pricing">
    <div class="container mx-auto px-6 text-center">
      <h2 class="text-2xl font-semibold mb-4">Pricing</h2>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
        <!-- Self-Serve Bookkeeping -->
        <mat-card class="p-6 rounded-lg flex flex-col">
          <h3 class="text-xl font-semibold mb-2">Self-Serve Bookkeeping</h3>
          <p class="text-base font-bold mb-4">$10 USD / Month</p>
          <div class="mb-4 p-3 bg-surface-container-low rounded">
            <p class="font-semibold">2-Month Free Trial</p>
            <p class="text-xs opacity-80">Free credits expire after trial period</p>
          </div>
          <ul class="flex-1 space-y-2 text-left mb-6">
            <li>• Email-based bill and receipt capture</li>
            <li>• Automatic categorization & vendor normalization</li>
            <li>• Unlimited records and email accounts</li>
            <li>• Basic P&L, cash flow, and expense reports</li>
            <li>• Analytics dashboard</li>
            <li>• Receipt scanning/upload</li>
            <li>• Export to Excel</li>
            <li>• Email notifications</li>
          </ul>
          <button matButton="filled" (click)="openAuth()">Start Free Trial</button>
        </mat-card>

        <!-- Custom Enterprise Reports -->
        <mat-card class="p-6 rounded-lg border-2 flex flex-col">
          <h3 class="text-xl font-semibold mb-2">Custom Enterprise Reports</h3>
          <p class="text-base font-bold mb-4">Custom Pricing</p>
          <div class="mb-4 p-3 bg-surface-container-low rounded">
            <p class="font-semibold">4-8 Week Implementation</p>
            <p class="text-xs opacity-80">From scoping to first report delivery</p>
          </div>
          <ul class="flex-1 space-y-2 text-left mb-6">
            <li>• Everything in Self-Serve, plus:</li>
            <li>• Custom P&L with ABC/standard costing</li>
            <li>• Variance analysis with root causes</li>
            <li>• Multi-entity consolidation</li>
            <li>• Direct ERP/database connections</li>
            <li>• Operational metrics integration</li>
            <li>• Dedicated implementation support</li>
            <li>• Ongoing model maintenance</li>
          </ul>
          <button matButton="filled" (click)="openContactUs()">Contact Sales</button>
        </mat-card>
      </div>
    </div>
  </section>

  <!-- Why Not Just Use -->
  <section class="py-6 bg-surface-container-high">
    <div class="container mx-auto px-6">
      <h2 class="text-2xl font-semibold text-center mb-4">Why Not Just Use...</h2>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        <mat-card>
          <mat-card-content>
            <div class="space-y-2">
              <h3 class="text-lg font-semibold">Power BI / Tableau / Looker?</h3>
              <p class="">
                Those are visualization tools. They don't know how to allocate overhead costs or
                calculate purchase price variance. We handle the accounting model, then give you the
                reports.
              </p>
            </div>
          </mat-card-content>
        </mat-card>
        <mat-card>
          <mat-card-content>
            <div class="space-y-2">
              <h3 class="text-lg font-semibold">Hire consultants?</h3>
              <p class="">
                Consultants build one-time reports in spreadsheets. We build repeatable, automated
                reports on your live data. First report takes 4-8 weeks, then it updates
                automatically.
              </p>
            </div>
          </mat-card-content>
        </mat-card>
        <mat-card>
          <mat-card-content>
            <div class="space-y-2">
              <h3 class="text-lg font-semibold">Implement SAP / Oracle?</h3>
              <p class="">
                If you have 18 months and $500K+, maybe you should. If you need custom financial
                reports in the next quarter, we're faster and more focused.
              </p>
            </div>
          </mat-card-content>
        </mat-card>
        <mat-card>
          <mat-card-content>
            <div class="space-y-2">
              <h3 class="text-lg font-semibold">QuickBooks / Xero?</h3>
              <p class="">
                Those work great for basic bookkeeping. When you need ABC costing, multi-entity
                consolidation, or custom operational metrics, you've outgrown them.
              </p>
            </div>
          </mat-card-content>
        </mat-card>
      </div>
    </div>
  </section>

  <!-- FAQ Section -->
  <section class="py-4" id="faq">
    <div class="container mx-auto px-6">
      <h2 class="text-2xl font-semibold text-center mb-8">Frequently Asked Questions</h2>
      <mat-accordion class="space-y-4 max-w-4xl mx-auto">
        <mat-expansion-panel>
          <mat-expansion-panel-header>
            <mat-panel-title>How long does the first custom report take?</mat-panel-title>
          </mat-expansion-panel-header>
          <p>
            4-8 weeks typically. Weeks 1-2: data connection and understanding your business model.
            Weeks 3-6: build and test the reporting framework. Weeks 7-8: refinement and delivery.
          </p>
        </mat-expansion-panel>

        <mat-expansion-panel>
          <mat-expansion-panel-header>
            <mat-panel-title>Are all these capabilities ready to use today?</mat-panel-title>
          </mat-expansion-panel-header>
          <p>
            We build custom reports to your specifications. Our data integration pipeline is
            production-ready. The specific accounting models (ABC costing, variance analysis, etc.)
            are built during your project based on your needs.
          </p>
        </mat-expansion-panel>

        <mat-expansion-panel>
          <mat-expansion-panel-header>
            <mat-panel-title>Do you replace my accounting system?</mat-panel-title>
          </mat-expansion-panel-header>
          <p>
            No. We connect to your existing systems (ERP, accounting software, databases) and build
            reports on top of them.
          </p>
        </mat-expansion-panel>

        <mat-expansion-panel>
          <mat-expansion-panel-header>
            <mat-panel-title>What if my data is messy?</mat-panel-title>
          </mat-expansion-panel-header>
          <p>
            Part of our process is data validation and normalization. We'll identify gaps and work
            with you to improve data quality.
          </p>
        </mat-expansion-panel>

        <mat-expansion-panel>
          <mat-expansion-panel-header>
            <mat-panel-title>Can I change the reports later?</mat-panel-title>
          </mat-expansion-panel-header>
          <p>
            Yes. After initial delivery, we support model changes as your business evolves (new cost
            centers, allocation rule changes, etc.).
          </p>
        </mat-expansion-panel>

        <mat-expansion-panel>
          <mat-expansion-panel-header>
            <mat-panel-title>What's the difference between the two paths?</mat-panel-title>
          </mat-expansion-panel-header>
          <p>
            Self-Serve Bookkeeping ($10/month) is for basic expense tracking and organization.
            Custom Enterprise Reports are for companies that need advanced financial models like ABC
            costing, variance analysis, and multi-entity consolidation. You can start with
            bookkeeping and upgrade when needed.
          </p>
        </mat-expansion-panel>

        <mat-expansion-panel>
          <mat-expansion-panel-header>
            <mat-panel-title>How does the free trial work?</mat-panel-title>
          </mat-expansion-panel-header>
          <p>
            You get 2 months of free access to Self-Serve Bookkeeping with included credits. After
            the trial, you can continue at $10/month or upgrade to custom enterprise reports if you
            need more advanced capabilities.
          </p>
        </mat-expansion-panel>

        <mat-expansion-panel>
          <mat-expansion-panel-header>
            <mat-panel-title>What accounting standards do you follow?</mat-panel-title>
          </mat-expansion-panel-header>
          <p>
            We follow GAAP/IFRS principles and work with your auditors to ensure methodology is
            defensible.
          </p>
        </mat-expansion-panel>
      </mat-accordion>
    </div>
  </section>

  <!-- Call to Action / Footer -->
  <section class="pt-12 pb-16 text-center bg-surface-container-high">
    <h2 class="text-2xl font-semibold mb-6">Get Started Today</h2>
    <p class="mb-8 max-w-2xl mx-auto">
      Start with self-serve bookkeeping to build your data foundation, or contact us for custom
      financial reports built on proper accounting frameworks.
    </p>
    <div class="flex flex-wrap gap-4 justify-center">
      <button matButton="filled" (click)="openContactUs()">Request Custom Report</button>
      <button matButton="outlined" [matTooltip]="'Self-Serve Bookkeeping'" (click)="openAuth()">
        @if (firebaseUser$()) {
          <div class="flex items-center">
            <div class="pr-2">Open Dashboard</div>
            <div>
              @if (isLoading$()) {
                <mat-progress-spinner class="small" mode="indeterminate"></mat-progress-spinner>
              }
            </div>
          </div>
        } @else {
          Start Free Trial
        }
      </button>
    </div>
  </section>
</app-billytics-public-container-component>
